import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import { RED_HOVER, YELLOW } from '../utils/brandColors';

Logo.propTypes = {
  disabledLink: PropTypes.bool,
  mini: PropTypes.bool,
  sx: PropTypes.object,
};

// Unique ID prefix so multiple Logo instances don't clash on shadow defs
let _logoId = 0;
const nextId = () => { _logoId += 1; return `pplogo${_logoId}`; };

export default function Logo({ disabledLink = false, mini = false, sx }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const id = nextId();

  const logo = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: disabledLink ? 'default' : 'pointer',

        // --- Mount animation for wordmark ---
        '& .pp-wordmark': {
          opacity: 0,
          animation: 'ppFadeSlide 0.5s ease forwards 0.2s',
        },
        '@keyframes ppFadeSlide': {
          '0%': { opacity: 0, transform: 'translateX(-4px)' },
          '100%': { opacity: 1, transform: 'translateX(0)' },
        },

        // --- Active state press-feedback ---
        '&:active': {
          transform: 'scale(0.96)',
          transition: 'transform 0.1s ease',
        },

        // --- Base card tilts (relative to local origins) ---
        '& .pp-new-card-left': {
          transform: 'rotate(-16deg)',
        },
        '& .pp-new-card-right': {
          transform: 'rotate(14deg)',
        },

        ...sx,
      }}
    >
      {/* Animated SVG Icon */}
      <Box
        className="pp-icon"
        component="svg"
        viewBox="140 25 220 220"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          filter: isDark
            ? `drop-shadow(0 1px 3px rgba(0, 0, 0, 0.45))`
            : `drop-shadow(0 1px 3px rgba(19, 19, 26, 0.12))`,
        }}
      >
        <defs>
          {/* Premium font styling inside the SVG */}
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@800;900&display=swap');
              .card-text {
                font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
                font-weight: 800;
                font-size: 20px;
                letter-spacing: -0.5px;
              }
            `}
          </style>

          {/* Premium shadows for optimized depth */}
          <filter id={`${id}_icon_shadow`} x="-20%" y="-20%" width="150%" height="150%">
            <feDropShadow
              dx="0" dy="10" stdDeviation="12"
              floodColor="#09090d"
              floodOpacity={isDark ? 0.35 : 0.3}
            />
            <feDropShadow
              dx="0" dy="3" stdDeviation="3"
              floodColor="#09090d"
              floodOpacity={isDark ? 0.15 : 0.15}
            />
          </filter>

          <filter id={`${id}_card_shadow`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000000" floodOpacity="0.3" />
            <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#000000" floodOpacity="0.15" />
          </filter>

        </defs>

        {/* Midnight Slate App Icon Base */}
        <rect
          x="165" y="45" width="170" height="170" rx="46"
          fill={isDark ? '#212430' : '#13131a'}
          stroke={isDark ? 'rgba(255, 255, 255, 0.12)' : 'none'}
          strokeWidth={isDark ? 3.5 : 0}
          filter={`url(#${id}_icon_shadow)`}
        />

        {/* Left Card (Solid White Card containing "That") */}
        <g transform="translate(210, 128)">
          <g className="pp-new-card-left" transform="rotate(-16)">
            <rect
              x="-38" y="-57" width="76" height="114" rx="14"
              fill="#ffffff"
              stroke="#13131a"
              strokeOpacity="0.08"
              strokeWidth="1.5"
              filter={`url(#${id}_card_shadow)`}
            />
            <text x="0" y="6" textAnchor="middle" className="card-text" fill="#2d2d34">
              That
            </text>
          </g>
        </g>

        {/* Right Card (Gold Yellow Card containing "This") */}
        <g transform="translate(285, 134)">
          <g className="pp-new-card-right" transform="rotate(14)">
            <rect
              x="-38" y="-57" width="76" height="114" rx="14"
              fill={YELLOW}
              stroke="#13131a"
              strokeOpacity="0.08"
              strokeWidth="1.5"
              filter={`url(#${id}_card_shadow)`}
            />
            <text x="0" y="6" textAnchor="middle" className="card-text" fill={RED_HOVER}>
              This
            </text>
          </g>
        </g>
      </Box>

      {/* Wordmark */}
      {!mini && (
        <Typography
          className="pp-wordmark"
          sx={{
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: 800,
            fontSize: '1.1rem',
            letterSpacing: -0.8,
            color: 'text.primary',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            userSelect: 'none',
            transition: 'letter-spacing 0.3s ease',
          }}
        >
          pick
          <Box
            component="span"
            className="pp-pls"
            sx={{
              color: 'text.primary',
              fontWeight: 900,
              transition: 'color 0.3s ease',
            }}
          >
            pls
          </Box>
        </Typography>
      )}
    </Box>
  );


  if (disabledLink) {
    return <>{logo}</>;
  }

  return (
    <RouterLink to="/" style={{ textDecoration: 'none' }}>
      {logo}
    </RouterLink>
  );
}

